import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useListaFiltrada } from "@/lib/filtros";
import { type FiltrosEquipos, filtrarEquipos } from "@/lib/filtros/equipos";

/** Criticidad admisible de un equipo (espejo del CHECK del esquema). */
export type Criticidad = "ALTA" | "MEDIA" | "BAJA";

/**
 * EquipoEstado es el estado calculado que devuelve el backend. Es BINARIO (#243): describe
 * SOLO la situación de seguridad en planta —un equipo está BLOQUEADO si alguno de sus
 * puntos sigue cubierto por un candado puesto—. `activo` y `loteable` son atributos del
 * catálogo con columna propia, no estados.
 */
export type EquipoEstado = "BLOQUEADO" | "DESBLOQUEADO";

/**
 * CandadoDeEquipo es uno de los candados que bloquean AHORA el equipo (#248), con el LOTO
 * de su bloqueo. Cada candado aparece una vez aunque cubra varios puntos del equipo.
 */
export type CandadoDeEquipo = {
  id_candado: number;
  codigo_candado: string;
  id_loto: number;
  numero_loto: string;
};

/**
 * PuntoBloqueadoDeEquipo es uno de los puntos del equipo que siguen cubiertos por algún
 * candado (#248). Cada punto aparece una vez aunque lo cubran varios candados.
 */
export type PuntoBloqueadoDeEquipo = {
  id_punto: number;
  nombre: string;
};

/**
 * Equipo es una máquina/activo con su área y estado calculado (GET /api/v1/equipos).
 * `codigo_equipo` (EQ-XXXX) lo asigna el servidor; el cliente nunca lo fija. `candados` y
 * `puntos_bloqueados` dicen QUÉ lo bloquea (#248): `[]` si está desbloqueado.
 */
export type Equipo = {
  id: number;
  codigo_equipo: string;
  nombre_equipo: string;
  descripcion: string | null;
  id_area: number;
  nombre_area: string;
  criticidad: Criticidad;
  loteable: boolean;
  activo: boolean;
  estado: EquipoEstado;
  candados: CandadoDeEquipo[];
  puntos_bloqueados: PuntoBloqueadoDeEquipo[];
  version: number;
  updated_at: string;
};

/** EquipoInput es el payload de alta/edición (POST/PUT /api/v1/equipos). */
export type EquipoInput = {
  nombre_equipo: string;
  descripcion: string | null;
  id_area: number;
  criticidad: Criticidad;
  loteable: boolean;
  activo: boolean;
};

/**
 * equiposQueryKey namespacea la caché de equipos POR obra: `["obra", obraId, "equipos"]`
 * (F7 #158). Aísla en la caché de Query los equipos de una obra de los de otra.
 */
export function equiposQueryKey(obraId: number) {
  return ["obra", obraId, "equipos"] as const;
}

/**
 * equiposQueryOptions lista los equipos vivos con su área y estado de la obra `obraId`.
 * Se refresca invalidando la clave tras cada mutación (create/update/delete), igual que
 * áreas (#27), ahora con la obra en la ruta y en la clave.
 */
export function equiposQueryOptions(obraId: number) {
  return queryOptions({
    queryKey: equiposQueryKey(obraId),
    queryFn: () => api.get<Equipo[]>(`/obras/${obraId}/equipos`),
  });
}

/**
 * useEquipos lista los equipos de la obra, opcionalmente FILTRADOS (#247): con red filtra
 * el servidor; sin red, la función pura sobre la lista base cacheada.
 */
export function useEquipos(obraId: number, filtros: FiltrosEquipos = {}) {
  return useListaFiltrada<Equipo>({
    claveBase: equiposQueryKey(obraId),
    url: `/obras/${obraId}/equipos`,
    filtros,
    filtrar: filtrarEquipos,
  });
}

export function useCreateEquipo(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EquipoInput) => api.post<Equipo>(`/obras/${obraId}/equipos`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: equiposQueryKey(obraId) }),
  });
}

export function useUpdateEquipo(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: EquipoInput }) =>
      api.put<Equipo>(`/obras/${obraId}/equipos/${id}`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: equiposQueryKey(obraId) }),
  });
}

export function useDeleteEquipo(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del<void>(`/obras/${obraId}/equipos/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: equiposQueryKey(obraId) }),
  });
}
